import React, {useEffect, createContext, useState, useMemo, useContext, useCallback} from "react";
import PropTypes from "prop-types";
import {consentManagementAPI} from "../../common/api/consentManagementAPI";
import {API_CONSENT_MANAGEMENT_TERMS_OF_USE} from "../../common/api/apiConstants";
import {createURLQuery} from "../../common/api/apiUtil";
import { useBreakpoints, useKeycloakFunctions } from "../../hooks";
import {LocaleContext} from "./LocaleProvider";
import TermsOfUseDialog from "../../component/dialog/termsOfUse/TermsOfUseDialog";
import {useLocation} from "../../common/common";
import { IMPRINT, MARKET_PLACE, POINTS_SHARES_SHARES } from "../../common/routes";

const MarketplaceConsentContext = createContext();

const MarketplaceConsentProvider = ({children}) => {
    const {initialized, authenticated, isAdmin} = useKeycloakFunctions();
    const {isExtraSmallScreen} = useBreakpoints();
    const {localeUppercase} = useContext(LocaleContext)[0];
    const {location} = useLocation();

    const [openTermsOfUse, setOpenTermsOfUse] = useState();
    const [isUserForbidden, setIsUserForbidden] = useState(false);
    const [termsOfUse, setTermsOfUse] = useState({});
    const [accepted, setAccepted] = useState(false);
    const [acceptedAlready, setAcceptedAlready] = useState(false);
    const [showLatestTermsOfUse, setShowLatestTermsOfUse] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dismissableDialog, setDismissableDialog] = useState(false);

    useEffect(() => {
        if((location?.pathname?.includes(MARKET_PLACE) && !location?.pathname?.includes(IMPRINT)) ||
            location?.pathname?.includes(POINTS_SHARES_SHARES)) {
            getTermsOfUse(location?.pathname?.includes(POINTS_SHARES_SHARES) ? {redirectOnError: false} : {redirectOnError: true});
        }
    }, [initialized, authenticated, location]);

    const getTermsOfUse = useCallback(({redirectOnError}) => {
        if (initialized && authenticated) {
            setLoading(true);
            setError(null);

            let parameters = {
                locale: localeUppercase,
            };

            return consentManagementAPI.get(API_CONSENT_MANAGEMENT_TERMS_OF_USE + createURLQuery(parameters),
                {redirectOnError}
            )
                .then(res => res.data)
                .then(response => {
                    setLoading(false);
                    if (response.accepted){
                        setAcceptedAlready(true);
                        setAccepted(true);
                        setShowLatestTermsOfUse(false);
                    } else if (!response.accepted){
                        if (response.terms_of_use_update_mode) {
                            setShowLatestTermsOfUse(true);
                        } else {
                            setTermsOfUse({"termsOfUse": response.terms_of_use});
                        }
                    }
                })
                .catch(err => {
                    if (err.response.status === 403) {
                        setIsUserForbidden(true);
                    }
                    setLoading(false);
                    setError(err);
                });
        }
    }, [initialized, authenticated]);

    const postAcceptanceTermsOfUse = useCallback(() => {
        setLoading(true);
        setError(null);
        let parameters = {
            locale: localeUppercase,
        };
        consentManagementAPI.post(API_CONSENT_MANAGEMENT_TERMS_OF_USE + createURLQuery(parameters))
            .then(res => res.data)
            .then(() => {
                setLoading(false);
                setAccepted(true);
            })
            .catch(err => {
                setAccepted(false);
                setLoading(false);
                setError(err);
            });
    }, []);

    const handleCloseTermsOfUseDialog = () => {
        setOpenTermsOfUse(false);
    };

    const handleSubmitAcceptTerms = () => {
        postAcceptanceTermsOfUse();
        setOpenTermsOfUse(false);
    };

    const openTerms = ({dismissable}) => {
        if (!isAdmin()) {
            setDismissableDialog(!dismissable);
            setOpenTermsOfUse(true);
        }
    };

    const consentData = useMemo (() => ({
        getTermsOfUse,
        openTerms,
        accepted,
        acceptedAlready,
        termsOfUse,
        loading,
        error,
        isUserForbidden
    }), [openTerms, accepted, acceptedAlready, termsOfUse, loading, error, isUserForbidden]);

    return (
        <MarketplaceConsentContext.Provider value={consentData}>
            {children}
            <TermsOfUseDialog
                isOpen={openTermsOfUse}
                isMobile={isExtraSmallScreen()}
                onCloseDialog={handleCloseTermsOfUseDialog}
                onSubmit={handleSubmitAcceptTerms}
                disableBackdropClick={dismissableDialog}
                disableCloseIcon
                termsOfUse={termsOfUse?.termsOfUse?.text}
                pageTitle={termsOfUse?.termsOfUse?.page_title}
                companyName={termsOfUse?.termsOfUse?.company?.company_name}
                placeMonthYear={termsOfUse?.termsOfUse?.place_month_year}
                showLatestTermsOfUse={showLatestTermsOfUse}
            />
        </MarketplaceConsentContext.Provider>
    );
};

MarketplaceConsentProvider.propTypes = {
    children: PropTypes.any
};

export {MarketplaceConsentContext};
export default MarketplaceConsentProvider;
export function useMarketplaceConsent () {
    return useContext(MarketplaceConsentContext);
}
